import { useState, useEffect, useCallback, useRef } from 'react';
import { BookOpen, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import purchaseOrderService, { type PurchaseOrderXero } from '../../services/purchaseOrders';
import { XeroContactPicker } from '../nursery/accounts/XeroContactCard';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import type { PurchaseOrder } from '../../types/spray';

/**
 * Where an order stands in the organisation's own Xero, on its page.
 *
 * Purchase orders go to Xero (switched on under Accounts → Xero) approved and
 * under our number, once sent to the supplier or marked as ordered, so the
 * supplier's bill can be made from the order there. Xero then marks it
 * Billed, which is read back and shown here: that is what accounts is
 * matching against.
 *
 * Renders nothing while purchase orders are not going to Xero. Anyone who can
 * read the order can send it again after a failure; only an Accounts Admin
 * chooses the supplier's Xero contact.
 */

// While an order is on its way, how often and how many times to look again.
const POLL_MS = 4000;
const POLL_TIMES = 8;

export default function PurchaseOrderXeroCard({ po }: { po: PurchaseOrder }) {
  const { hasAccess: isAccountsAdmin } = useModuleLicense('accounts', 'admin');
  const [data, setData] = useState<PurchaseOrderXero | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const polls = useRef(0);

  const load = useCallback(async () => {
    try {
      const res = await purchaseOrderService.getXero(po.id);
      if (res.success && res.data) setData(res.data);
    } catch {
      // The order still reads without its Xero side.
    }
  }, [po.id]);

  // Read again whenever the order itself is read again: a change to it may
  // have sent it to Xero.
  useEffect(() => {
    polls.current = 0;
    void load();
  }, [load, po]);

  // On its way: look again shortly, a few times, so the page says where it got to.
  useEffect(() => {
    const waiting = data?.sync_status === 'pending' || data?.sync_status === 'pushing';
    if (!waiting || polls.current >= POLL_TIMES) return undefined;
    const t = setTimeout(() => { polls.current += 1; void load(); }, POLL_MS);
    return () => clearTimeout(t);
  }, [data, load]);

  if (!data?.active) return null;

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await purchaseOrderService.retryXero(po.id);
      if (res.success && res.data) setData(res.data);
      else setError(errorMessage(res, 'Could not send it to Xero'));
    } catch (e) {
      setError(errorMessage(e, 'Could not send it to Xero'));
    } finally {
      setBusy(false);
    }
  };

  const number = po.reference_number ?? `PO #${po.id}`;
  const supplier = data.supplier_name ?? 'the supplier';
  const inXero = data.sync_status === 'synced' && !!data.xero_purchase_order_id;
  const notYet = !data.sync_status && !data.xero_purchase_order_id;

  let state: { tone: 'neutral' | 'good' | 'bad'; text: string };
  if (data.sync_status === 'failed') {
    state = { tone: 'bad', text: `Not sent to Xero: ${data.sync_error || 'Xero gave no reason.'}` };
  } else if (data.sync_status === 'pending' || data.sync_status === 'pushing') {
    state = { tone: 'neutral', text: 'On its way to Xero.' };
  } else if (inXero && data.xero_status === 'BILLED') {
    state = { tone: 'good', text: `Billed in Xero: ${supplier}'s bill has been made from this order.` };
  } else if (inXero && data.xero_status === 'DELETED') {
    state = { tone: 'neutral', text: 'Deleted in Xero.' };
  } else if (inXero) {
    state = {
      tone: 'neutral',
      text: `In Xero as ${number}, approved. When ${supplier}'s bill arrives, make it from this order in Xero (New bill, then Copy from purchase order), and Xero marks the order billed.`,
    };
  } else if (po.status === 'draft') {
    state = { tone: 'neutral', text: 'Goes to Xero as an approved purchase order once it is sent to the supplier or marked as ordered.' };
  } else if (po.status === 'cancelled') {
    state = { tone: 'neutral', text: 'Not in Xero.' };
  } else {
    state = { tone: 'neutral', text: 'Not in Xero yet.' };
  }

  const toneCls = state.tone === 'good'
    ? 'bg-green-50 border-green-200 text-green-800'
    : state.tone === 'bad'
      ? 'bg-red-50 border-red-200 text-red-700'
      : 'bg-neutral-50 border-neutral-200 text-neutral-700';
  // A note Xero left on an order that did go (billed before it was cancelled here).
  const note = data.sync_status === 'synced' ? data.sync_error : null;
  const canSend = data.sync_status === 'failed' || (notYet && !['draft', 'cancelled'].includes(po.status));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-3">
      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-gray-500" /> Xero
      </h2>

      <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${toneCls}`} role="status">
        {state.tone === 'good' && <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />}
        {state.tone === 'bad' && <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />}
        <p className="min-w-0 break-words">{state.text}</p>
      </div>
      {note && <p className="text-sm text-amber-800 break-words">{note}</p>}
      {error && <p role="alert" className="text-sm text-red-700 break-words">{error}</p>}
      {canSend && (
        <Button variant="outline" size="sm" onClick={send} disabled={busy}>
          <RefreshCw className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} />
          {busy ? 'Sending to Xero…' : data.sync_status === 'failed' ? 'Try again' : 'Send to Xero'}
        </Button>
      )}

      <div className="border-t border-gray-100 pt-3 space-y-1">
        <p className="text-xs text-gray-500">{supplier} in Xero</p>
        {data.contact ? (
          <p className="text-sm font-medium text-gray-900 break-words">{data.contact.name}</p>
        ) : data.contact_id ? (
          <p className="text-sm text-red-700 break-words">{data.contact_error || 'The linked contact could not be read from Xero.'}</p>
        ) : (
          <p className="text-sm text-gray-700 break-words">
            Not linked yet. The first order sent looks in Xero for a contact named exactly {supplier}, and makes
            one if there is none.
          </p>
        )}
        {data.supplier_name && (isAccountsAdmin ? (
          <Button variant="outline" size="sm" onClick={() => setPicking(true)}>
            {data.contact_id ? 'Change the Xero contact' : 'Choose a Xero contact'}
          </Button>
        ) : (
          <p className="text-xs text-gray-500 break-words">An Accounts Admin can choose which Xero contact this is.</p>
        ))}
      </div>

      {picking && data.supplier_name && (
        <XeroContactPicker
          name={data.supplier_name}
          intro={`Orders to ${data.supplier_name} go to the contact you choose, this one and every later one. Orders already in Xero stay on the contact they went to.`}
          unlinked={`Without a link, the next order looks in Xero for a contact named exactly ${data.supplier_name}, and makes a new one if there is none.`}
          currentContactId={data.contact_id}
          onClose={() => setPicking(false)}
          onSave={async (contactId) => {
            const res = await purchaseOrderService.setSupplierXeroContact(po.id, contactId);
            if (!res.success || !res.data) return errorMessage(res, 'Could not save the Xero contact');
            setData(res.data);
            setPicking(false);
            return null;
          }}
        />
      )}
    </div>
  );
}
